import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import { MdCheck, MdClose } from 'react-icons/md';
import '../styles/ProfilePictureCropper.css';

const SHAPE_OPTIONS = [
  { value: 'circle', label: 'Circle' },
  { value: 'square', label: 'Square' },
  { value: 'rounded', label: 'Rounded Square' },
];

export default function ProfilePictureCropper({ imageSrc, onCropComplete, onClose }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [selectedShape, setSelectedShape] = useState('circle');

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  const getRadianAngle = (degreeValue) => {
    return (degreeValue * Math.PI) / 180;
  };

  const rotateSize = (width, height, rotation) => {
    const rotRad = getRadianAngle(rotation);
    return {
      width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
      height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
    };
  };

  const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    const rotRad = getRadianAngle(rotation);
    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
      image.width,
      image.height,
      rotation
    );

    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(rotRad);
    ctx.scale(1, 1);
    ctx.translate(-image.width / 2, -image.height / 2);

    ctx.drawImage(image, 0, 0);

    const data = ctx.getImageData(
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height
    );

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.putImageData(data, 0, 0);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      }, 'image/png');
    });
  };

  const handleApply = async () => {
    if (!croppedAreaPixels) return;
    
    const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
    if (croppedImage) {
      onCropComplete(croppedImage, selectedShape);
    }
  };

  const modal = (
    <div className="cropper-modal-backdrop" onClick={onClose}>
      <div
        className="cropper-modal-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-cropper-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cropper-modal-header">
          <h3 id="profile-cropper-title">Crop Your Profile Picture</h3>
          <button className="cropper-close-btn" onClick={onClose} aria-label="Close profile picture cropper">
            <MdClose size={24} />
          </button>
        </div>

        <div className="cropper-content">
          <div className="cropper-wrapper">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={onCropCompleteCallback}
              cropShape={selectedShape === 'circle' ? 'round' : 'rect'}
              showGrid={false}
            />
          </div>

          <div className="cropper-controls">
            <div className="shape-selector">
              <label>Shape:</label>
              <div className="shape-options">
                {SHAPE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`shape-btn ${selectedShape === option.value ? 'active' : ''}`}
                    onClick={() => setSelectedShape(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="zoom-control">
              <label>Zoom:</label>
              <input
                type="range"
                aria-label="Profile picture zoom"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="zoom-slider"
              />
              <span>{zoom.toFixed(1)}x</span>
            </div>
          </div>
        </div>

        <div className="cropper-actions">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-apply" onClick={handleApply}>
            <MdCheck size={20} />
            Apply Crop
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
